import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMemberList } from '../../hooks/useMemberList';
import { useBrgList } from '../../hooks/useBrgList';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { EmptyState } from '../../components/shared/EmptyState';
import { MemberInfoPopup } from './MemberInfoPopup';
import { MemberListItemDto } from '../../types';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';

function formatRoleCode(roleCode: string): string {
  if (roleCode === 'Multiple') return roleCode;
  return roleCode
    .replace(/^Co([A-Z])/, 'Co-$1')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

function getRoleDisplay(member: MemberListItemDto, selectedBrgIds: number[]): string {
  if (selectedBrgIds.length === 0) return formatRoleCode(member.role);

  const relevantMemberships = member.brgMemberships.filter((m) =>
    selectedBrgIds.includes(parseInt(m.brgId, 10))
  );
  if (relevantMemberships.length === 0) return formatRoleCode(member.role);

  const uniqueRoles = [...new Set(relevantMemberships.map((m) => m.role))];
  return uniqueRoles.length > 1 ? 'Multiple' : formatRoleCode(uniqueRoles[0]);
}

export function MembersPage() {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedBrgIds, setSelectedBrgIds] = useState<number[]>([]);
  const [selectedMemberUserId, setSelectedMemberUserId] = useState<string | null>(null);

  const isAllSelected = selectedBrgIds.length === 0;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const brgQuery = useBrgList();
  const memberQuery = useMemberList({
    search: debouncedSearch || undefined,
    brgIds: selectedBrgIds.length > 0 ? selectedBrgIds.map(String) : undefined,
  });

  const filteredMembers = useMemo(() => {
    if (!memberQuery.data) return [];
    return memberQuery.data.filter((m) => {
      if (!m.isActive && !isAllSelected) return false;
      return true;
    });
  }, [memberQuery.data, isAllSelected]);

  function toggleBrgFilter(brgId: number) {
    setSelectedBrgIds((prev) =>
      prev.includes(brgId) ? prev.filter((id) => id !== brgId) : [...prev, brgId]
    );
  }

  const handleClosePopup = useCallback(() => setSelectedMemberUserId(null), []);

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search members..."
          style={{
            width: '100%',
            maxWidth: '600px',
            padding: '9px 14px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '0.95rem',
            outline: 'none',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

        {/* Member list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {memberQuery.isLoading && <LoadingSpinner message="Loading members..." />}
          {memberQuery.isError && (
            <ErrorMessage
              message={memberQuery.error?.message ?? 'Failed to load members.'}
              onRetry={() => void memberQuery.refetch()}
            />
          )}

          {!memberQuery.isLoading && !memberQuery.isError && (
            <>
              {filteredMembers.length === 0 ? (
                <EmptyState
                  title="No members found"
                  message={
                    debouncedSearch || selectedBrgIds.length > 0
                      ? 'Try adjusting your search or filter.'
                      : 'No members exist yet.'
                  }
                />
              ) : (
                <>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#111', marginBottom: '10px' }}>
                    Members ({filteredMembers.length})
                  </div>
                  <MembersGrid
                    members={filteredMembers}
                    onRowClick={(userId: string) => setSelectedMemberUserId(userId)}
                    getRoleDisplay={(member) => getRoleDisplay(member, selectedBrgIds)}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* BRG Filter — right side */}
        <div
          style={{
            width: '200px',
            flexShrink: 0,
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            padding: '16px',
          }}
        >
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 700, color: '#1a1a1a' }}>
            BRG Filter
          </h3>

          <label style={filterLabelStyle(isAllSelected)}>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={() => setSelectedBrgIds([])}
              style={{ cursor: 'pointer' }}
            />
            All
          </label>

          {brgQuery.isLoading && (
            <p style={{ fontSize: '0.8rem', color: '#888', margin: '8px 0' }}>Loading...</p>
          )}

          {brgQuery.data?.map((brg) => {
            const isChecked = selectedBrgIds.includes(brg.brgId);
            return (
              <label key={brg.brgId} style={filterLabelStyle(isChecked)}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleBrgFilter(brg.brgId)}
                  style={{ cursor: 'pointer' }}
                />
                {brg.name}
              </label>
            );
          })}
        </div>
      </div>

      <MemberInfoPopup userId={selectedMemberUserId} onClose={handleClosePopup} />
    </div>
  );
}

function filterLabelStyle(isSelected: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '5px 4px',
    fontSize: '0.875rem',
    fontWeight: isSelected ? 600 : 400,
    color: '#333',
  };
}

// MembersGrid Component using AG Grid
interface MembersGridProps {
  members: MemberListItemDto[];
  onRowClick: (userId: string) => void;
  getRoleDisplay: (member: MemberListItemDto) => string;
}

function MembersGrid({ members, onRowClick, getRoleDisplay }: MembersGridProps) {
  const gridRef = useRef<AgGridReact>(null);

  // Custom CSS for AG Grid styling (matching Budget/Recognition style)
  const customGridStyles = `
    .ag-theme-quartz {
      --ag-header-background-color: #1a3bcc;
      --ag-header-foreground-color: #fff;
      --ag-header-cell-text-color: #fff;
      --ag-header-cell-padding: 8px 10px;
      --ag-header-cell-hover-background-color: #1530a8;
      --ag-header-cell-moving-background-color: #1530a8;
      --ag-font-size: 11px;
      --ag-cell-horizontal-padding: 10px;
      --ag-cell-vertical-padding: 8px;
      --ag-row-height: 32px;
      --ag-row-border-color: #f0f0f0;
      background-color: #ffffff;
      border: none;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-header {
      font-weight: 700;
      font-size: 11px;
      height: 32px;
      background-color: #1a3bcc;
    }
    
    .ag-theme-quartz .ag-header-cell {
      background-color: #1a3bcc !important;
      color: #fff !important;
      border-right: 1px solid #1530a8;
      font-weight: 700;
    }
    
    .ag-theme-quartz .ag-header-cell:last-child {
      border-right: none;
    }
    
    .ag-theme-quartz .ag-row {
      border-bottom: 1px solid #f0f0f0;
      height: 32px;
      background-color: #ffffff;
    }
    
    .ag-theme-quartz .ag-row:last-child {
      border-bottom: none;
    }
    
    .ag-theme-quartz .ag-row:hover {
      background-color: #f7f9ff !important;
    }
    
    .ag-theme-quartz .ag-cell {
      font-size: 11px;
      color: #333;
      padding: 8px 10px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
    
    .ag-theme-quartz .ag-root {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    }
    
    .ag-theme-quartz .ag-root-wrapper {
      border: none;
    }
    
    .ag-theme-quartz .ag-center-cols-viewport {
      overflow-x: auto;
    }
  `;

  interface MemberRow {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    role: string;
  }

  const columnDefs: ColDef<MemberRow>[] = useMemo(
    () => [
      {
        field: 'isActive',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 80,
        cellRenderer: (params: any) => (params.value ? 'Active' : 'Inactive'),
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 150,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'role',
        headerName: 'Role',
        flex: 1,
        minWidth: 120,
      },
    ],
    []
  );

  const gridOptions: GridOptions<MemberRow> = {
    domLayout: 'normal',
    rowHeight: 32,
    headerHeight: 32,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: false,
    },
    suppressColumnVirtualisation: false,
    paginationPageSize: 15,
    suppressCellFocus: true,
    suppressMovableColumns: true,
  };

  const rowData: MemberRow[] = useMemo(
    () =>
      members.map((member) => ({
        userId: member.userId,
        firstName: member.firstName || '—',
        lastName: member.lastName || '—',
        email: member.email || '—',
        isActive: member.isActive,
        role: getRoleDisplay(member),
      })),
    [members, getRoleDisplay]
  );

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        overflow: 'hidden',
        height: '500px',
      }}
    >
      <style>{customGridStyles}</style>
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact<MemberRow>
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          gridOptions={gridOptions}
          defaultColDef={{
            resizable: false,
            sortable: false,
            filter: false,
          }}
          onRowClicked={(event) => onRowClick(event.data!.userId)}
          rowStyle={{
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
        />
      </div>
    </div>
  );
}
